import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createNote, fetchMyNotes, updateNote } from '../../features/notes/services/noteService';
import { addNotification } from '../../store/slices/uiSlice';
import { fetchUsers } from '../../store/slices/userSlice';
import {
  canLiveNativeCaption,
  canNativeCaption,
  createPcmTap,
  encodeWav,
  mergeFloat32,
  resampleTo16k,
} from '../../utils/nativeCaptionAudio';
import { applyVoiceTaskFromCaption } from '../../utils/voiceTaskCommands';
import { isAudioCaptionEnabled, subscribeAudioCaptionEnabled } from '../../utils/audioCaptionPrefs';
import { speechRecognitionLang } from '../../constants/chatLanguages';

const BUBBLE_SIZE = 50;
const POSITION_KEY = 'audioCaptionBubblePosition';
const AUDIO_NOTE_TITLE = /^from audio #(\d+)$/i;
const SpeechRecognitionAPI =
  typeof window !== 'undefined'
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null;

const isElectronShell = () =>
  typeof window !== 'undefined' && Boolean(window.workloopRealm);

const escapeHtml = (text) =>
  String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const stripNoteHtml = (html) =>
  String(html || '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/p>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const PAUSE_NEWLINE_MS = 1200;

const mergeWords = (leftRaw, rightRaw) => {
  const left = String(leftRaw || '').replace(/\s+/g, ' ').trim();
  const right = String(rightRaw || '').replace(/\s+/g, ' ').trim();
  if (!left) return right;
  if (!right) return left;
  if (left.includes(right)) return left;
  if (right.startsWith(left)) return right;
  const prevWords = left.split(' ');
  const nextWords = right.split(' ');
  const max = Math.min(prevWords.length, nextWords.length, 10);
  let overlap = 0;
  for (let count = max; count > 0; count -= 1) {
    if (prevWords.slice(-count).join(' ').toLowerCase() === nextWords.slice(0, count).join(' ').toLowerCase()) {
      overlap = count;
      break;
    }
  }
  return `${left} ${nextWords.slice(overlap).join(' ')}`.replace(/\s+/g, ' ').trim();
};

const mergeTranscript = (prev, next, newParagraph = false) => {
  const left = String(prev || '').replace(/[^\S\n]+/g, ' ').replace(/\n{3,}/g, '\n\n').trim();
  const right = String(next || '').replace(/\s+/g, ' ').trim();
  if (!left) return right;
  if (!right) return left;
  if (newParagraph) {
    const lastLine = left.split('\n').pop() || '';
    if (lastLine.includes(right)) return left;
    return `${left}\n${right}`;
  }
  const lines = left.split('\n');
  const last = lines.pop() || '';
  lines.push(mergeWords(last, right));
  return lines.join('\n');
};

const nextAudioNoteTitle = async () => {
  let next = 1;
  try {
    const notes = await fetchMyNotes();
    (Array.isArray(notes) ? notes : []).forEach((note) => {
      const match = String(note?.title || '').trim().match(AUDIO_NOTE_TITLE);
      if (!match) return;
      const number = Number.parseInt(match[1], 10);
      if (Number.isFinite(number) && number >= next) next = number + 1;
    });
  } catch {
    // Keep #1 if the list cannot be loaded.
  }
  return `from audio #${next}`;
};

const captionToNoteHtml = (plain) => {
  const text = String(plain || '').trim();
  if (!text) return '';
  const paragraphs = text.split(/\n+/).filter(Boolean);
  return paragraphs.map((line) => `<p>${escapeHtml(line)}</p>`).join('');
};

const MicIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3a3 3 0 00-3 3v6a3 3 0 006 0V6a3 3 0 00-3-3z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 10v2a7 7 0 01-14 0v-2M12 19v2" />
  </svg>
);

const DraggableAudioCaptionBubble = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [position, setPosition] = useState({ x: window.innerWidth - 80, y: 150 });
  const [isDragging, setIsDragging] = useState(false);
  const [listening, setListening] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(() => isAudioCaptionEnabled());
  const [livePreview, setLivePreview] = useState('');
  const livePreviewRef = useRef('');
  const bubbleRef = useRef(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const dragMovedRef = useRef(false);
  const pointerStartRef = useRef({ x: 0, y: 0 });
  const listeningRef = useRef(false);
  const recognitionRef = useRef(null);
  const streamRef = useRef(null);
  const tapRef = useRef(null);
  const liveUnsubRef = useRef(null);
  const tickRef = useRef(null);
  const pcmRef = useRef([]);
  const busyRef = useRef(false);
  const livePushRef = useRef(false);
  const fatalRef = useRef(false);
  const noteIdRef = useRef(null);
  const noteTitleRef = useRef('');
  const noteCreateLockRef = useRef(null);
  const plainRef = useRef('');
  const htmlRef = useRef('');
  const saveTimerRef = useRef(null);
  const lastSpeechAtRef = useRef(0);
  const stickyNewLineRef = useRef(false);
  const startWebSpeechRef = useRef(() => false);
  const restartSpeechRef = useRef(() => {});
  const restartTimerRef = useRef(null);
  const lastResultAtRef = useRef(0);
  const speechStartingRef = useRef(false);
  const restartFailsRef = useRef(0);

  const constrainPosition = (pos) => {
    const maxX = window.innerWidth - BUBBLE_SIZE;
    const maxY = window.innerHeight - BUBBLE_SIZE;
    return {
      x: Math.max(0, Math.min(pos.x || 0, maxX)),
      y: Math.max(0, Math.min(pos.y || 0, maxY)),
    };
  };

  useEffect(() => {
    if (!user) return;
    const saved = localStorage.getItem(POSITION_KEY);
    if (!saved) {
      setPosition(constrainPosition({ x: window.innerWidth - BUBBLE_SIZE - 20, y: 150 }));
      return;
    }
    try {
      setPosition(constrainPosition(JSON.parse(saved)));
    } catch {
      setPosition(constrainPosition({ x: window.innerWidth - BUBBLE_SIZE - 20, y: 150 }));
    }
  }, [user]);

  useEffect(() => {
    if (user) localStorage.setItem(POSITION_KEY, JSON.stringify(position));
  }, [position, user]);

  useEffect(() => {
    const onResize = () => setPosition((prev) => constrainPosition(prev));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const noteIdOf = (created) => created?.id || created?._id || null;

  const ensureNote = useCallback(async (html = '') => {
    if (noteIdRef.current) return noteIdRef.current;
    if (noteCreateLockRef.current) {
      await noteCreateLockRef.current;
      return noteIdRef.current;
    }
    noteCreateLockRef.current = (async () => {
      const title = await nextAudioNoteTitle();
      noteTitleRef.current = title;
      const created = await createNote({
        title,
        contentHtml: html || '<p></p>',
        attachments: [],
        sensitiveBlocks: [],
      });
      noteIdRef.current = noteIdOf(created);
    })();
    try {
      await noteCreateLockRef.current;
    } finally {
      noteCreateLockRef.current = null;
    }
    window.dispatchEvent(new CustomEvent('workloop:notes-changed'));
    return noteIdRef.current;
  }, []);

  const flushSave = useCallback(async () => {
    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    const html = htmlRef.current || '';
    if (!stripNoteHtml(html) && !noteIdRef.current) return;
    try {
      const noteId = await ensureNote(html);
      if (noteId && stripNoteHtml(html)) {
        await updateNote(noteId, { contentHtml: html });
        window.dispatchEvent(new CustomEvent('workloop:notes-changed'));
      }
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: error?.response?.data?.error || error?.message || 'Failed to save caption note',
      }));
    }
  }, [dispatch, ensureNote]);

  const scheduleSave = useCallback(() => {
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(() => {
      flushSave();
    }, 800);
  }, [flushSave]);

  const commitCaption = useCallback((text, isFinal = true) => {
    const piece = String(text || '').replace(/\s+/g, ' ').trim();
    if (!piece) return;
    livePreviewRef.current = piece;
    setLivePreview(piece);
    const now = Date.now();
    lastResultAtRef.current = now;
    const prevPlain = plainRef.current;
    const paused = Boolean(prevPlain) && lastSpeechAtRef.current > 0
      && (now - lastSpeechAtRef.current >= PAUSE_NEWLINE_MS);
    if (paused) stickyNewLineRef.current = true;
    lastSpeechAtRef.current = now;
    const merged = mergeTranscript(prevPlain, piece, stickyNewLineRef.current);
    if (!merged) return;
    if (isFinal) {
      stickyNewLineRef.current = false;
      plainRef.current = merged;
      if (merged !== prevPlain) {
        const delta = prevPlain && merged.toLowerCase().startsWith(prevPlain.toLowerCase())
          ? merged.slice(prevPlain.length)
          : piece;
        applyVoiceTaskFromCaption((delta || piece).replace(/\n/g, ' ').trim());
      }
    }
    const html = captionToNoteHtml(merged);
    if (html === htmlRef.current) return;
    htmlRef.current = html;
    scheduleSave();
  }, [scheduleSave]);

  const stopListeningHardware = useCallback(() => {
    livePushRef.current = false;
    if (tickRef.current) {
      window.clearTimeout(tickRef.current);
      tickRef.current = null;
    }
    if (restartTimerRef.current) {
      window.clearTimeout(restartTimerRef.current);
      restartTimerRef.current = null;
    }
    const recognition = recognitionRef.current;
    recognitionRef.current = null;
    if (recognition) {
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      try {
        recognition.abort?.() || recognition.stop?.();
      } catch {
        // already stopped
      }
    }
    try { liveUnsubRef.current?.(); } catch { /* ignore */ }
    liveUnsubRef.current = null;
    try { window.workloopRealm?.stopLiveCaption?.(); } catch { /* ignore */ }
    const tap = tapRef.current;
    tapRef.current = null;
    try { tap?.disconnect?.(); } catch { /* ignore */ }
    try { tap?.ownedStream?.getTracks?.().forEach((track) => track.stop()); } catch { /* ignore */ }
    streamRef.current?.getTracks?.().forEach((track) => track.stop());
    streamRef.current = null;
    pcmRef.current = [];
    busyRef.current = false;
  }, []);

  const scheduleSpeechRestart = useCallback((delay = 50) => {
    if (!listeningRef.current || fatalRef.current) return;
    if (restartTimerRef.current) window.clearTimeout(restartTimerRef.current);
    const wait = Math.min(800, Math.max(40, delay + restartFailsRef.current * 80));
    restartTimerRef.current = window.setTimeout(() => {
      restartTimerRef.current = null;
      if (!listeningRef.current || fatalRef.current) return;
      if (recognitionRef.current || speechStartingRef.current) return;
      startWebSpeechRef.current?.();
    }, wait);
  }, []);

  const ensureMicStream = useCallback(async () => {
    const live = streamRef.current?.getAudioTracks?.().some((track) => track.readyState === 'live');
    if (live) return streamRef.current;
    try {
      streamRef.current?.getTracks?.().forEach((track) => track.stop());
    } catch { /* ignore */ }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;
    return stream;
  }, []);

  const startWebSpeech = useCallback(() => {
    if (isElectronShell() || !SpeechRecognitionAPI || fatalRef.current || !listeningRef.current) return false;
    if (recognitionRef.current || speechStartingRef.current) return true;
    speechStartingRef.current = true;
    try {
      const recognition = new SpeechRecognitionAPI();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;
      recognition.lang = speechRecognitionLang(user?.preferredLanguage);
      recognition.onstart = () => {
        restartFailsRef.current = 0;
        speechStartingRef.current = false;
      };
      recognition.onresult = (event) => {
        lastResultAtRef.current = Date.now();
        restartFailsRef.current = 0;
        let interim = '';
        let finals = '';
        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          const piece = event.results[i][0]?.transcript || '';
          if (event.results[i].isFinal) finals += ` ${piece}`;
          else interim += piece;
        }
        const finalText = finals.replace(/\s+/g, ' ').trim();
        const interimText = interim.replace(/\s+/g, ' ').trim();
        if (finalText) commitCaption(finalText, true);
        else if (interimText) commitCaption(interimText, false);
      };
      recognition.onerror = (event) => {
        const err = event?.error;
        if (err === 'not-allowed' || err === 'service-not-allowed') {
          fatalRef.current = true;
          speechStartingRef.current = false;
          dispatch(addNotification({
            type: 'error',
            message: 'Microphone or speech permission was denied',
          }));
          listeningRef.current = false;
          setListening(false);
          stopListeningHardware();
          return;
        }
        // no-speech / aborted fire on a 3–4s pause. Do not restart here —
        // Chrome throws if start() runs before onend. onend reopens instantly.
      };
      recognition.onend = () => {
        speechStartingRef.current = false;
        if (recognitionRef.current === recognition) {
          recognitionRef.current = null;
        }
        if (!listeningRef.current || fatalRef.current) return;
        const leftover = String(livePreviewRef.current || '').trim();
        if (leftover) commitCaption(leftover, true);
        try {
          startWebSpeechRef.current?.();
        } catch {
          scheduleSpeechRestart(60);
        }
      };
      recognitionRef.current = recognition;
      try {
        recognition.start();
        window.setTimeout(() => {
          if (speechStartingRef.current && recognitionRef.current === recognition) {
            speechStartingRef.current = false;
          }
        }, 800);
        return true;
      } catch {
        recognitionRef.current = null;
        speechStartingRef.current = false;
        restartFailsRef.current += 1;
        scheduleSpeechRestart(400);
        return false;
      }
    } catch {
      recognitionRef.current = null;
      speechStartingRef.current = false;
      restartFailsRef.current += 1;
      scheduleSpeechRestart(500);
      return false;
    }
  }, [commitCaption, dispatch, scheduleSpeechRestart, stopListeningHardware, user?.preferredLanguage]);

  startWebSpeechRef.current = startWebSpeech;

  const restartWebSpeech = useCallback(() => {
    if (!listeningRef.current || fatalRef.current) return;
    if (!recognitionRef.current && !speechStartingRef.current) {
      startWebSpeechRef.current?.();
    }
  }, []);

  restartSpeechRef.current = restartWebSpeech;

  const startNativeCaptions = useCallback(async () => {
    let stream = streamRef.current;
    if (!stream) {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch {
        dispatch(addNotification({
          type: 'error',
          message: 'Allow microphone access to capture captions',
        }));
        return false;
      }
    }
    if (!listeningRef.current) {
      stream.getTracks().forEach((track) => track.stop());
      return false;
    }
    streamRef.current = stream;
    const tap = createPcmTap(stream, {
      onFloat32: (copy) => {
        if (!listeningRef.current) return;
        pcmRef.current.push(copy);
        if (pcmRef.current.length > 400) pcmRef.current = pcmRef.current.slice(-400);
      },
      shouldPushLive: () => livePushRef.current && listeningRef.current,
    });
    if (tap) tap.ownedStream = stream;
    tapRef.current = tap;

    if (canLiveNativeCaption()) {
      try {
        const unsubCaption = window.workloopRealm.onLiveCaption?.((text) => {
          lastResultAtRef.current = Date.now();
          commitCaption(text, true);
        });
        const unsubEnded = window.workloopRealm.onLiveCaptionEnded?.(() => {
          livePushRef.current = false;
        });
        liveUnsubRef.current = () => {
          try { unsubCaption?.(); } catch { /* ignore */ }
          try { unsubEnded?.(); } catch { /* ignore */ }
        };
        const started = await window.workloopRealm.startLiveCaption();
        if (listeningRef.current && started && started.ok !== false) {
          livePushRef.current = true;
        } else {
          livePushRef.current = false;
          try { await window.workloopRealm?.stopLiveCaption?.(); } catch { /* ignore */ }
        }
      } catch {
        livePushRef.current = false;
      }
    }

    if (!canNativeCaption() || !tap) {
      dispatch(addNotification({
        type: 'error',
        message: 'Live captions are not supported here. Try Chrome on the web.',
      }));
      return false;
    }

    const tick = async () => {
      if (!listeningRef.current) return;
      if (livePushRef.current && Date.now() - lastResultAtRef.current > 5000) {
        livePushRef.current = false;
      }
      if (!livePushRef.current && !busyRef.current && pcmRef.current.length) {
        busyRef.current = true;
        const rate = tap.context.sampleRate || 16000;
        const all = mergeFloat32(pcmRef.current);
        const keep = Math.max(1, Math.floor(rate * 6));
        const windowed = all.length > keep ? all.subarray(all.length - keep) : all;
        try {
          const text = String(await window.workloopRealm.transcribeWav(
            encodeWav(resampleTo16k(windowed, rate), 16000),
          ) || '').trim();
          if (text && listeningRef.current) commitCaption(text, true);
        } catch {
          // ignore
        }
        busyRef.current = false;
      }
      if (listeningRef.current) {
        tickRef.current = window.setTimeout(tick, livePushRef.current ? 2200 : 1400);
      }
    };
    tickRef.current = window.setTimeout(tick, 900);
    return true;
  }, [commitCaption, dispatch]);

  const stopListening = useCallback(async () => {
    listeningRef.current = false;
    setListening(false);
    const leftover = String(livePreviewRef.current || '').trim();
    if (leftover) commitCaption(leftover, true);
    livePreviewRef.current = '';
    setLivePreview('');
    stopListeningHardware();
    await flushSave();
    if (noteIdRef.current) {
      window.dispatchEvent(new CustomEvent('workloop:open-notes'));
    }
  }, [commitCaption, flushSave, stopListeningHardware]);

  const startListening = useCallback(async () => {
    fatalRef.current = false;
    noteIdRef.current = null;
    noteTitleRef.current = '';
    noteCreateLockRef.current = null;
    dispatch(fetchUsers());
    plainRef.current = '';
    htmlRef.current = '';
    lastSpeechAtRef.current = 0;
    lastResultAtRef.current = Date.now();
    stickyNewLineRef.current = false;
    livePreviewRef.current = '';
    setLivePreview('');
    listeningRef.current = true;
    setListening(true);

    if (window.workloopRealm?.ensureMicrophoneAccess) {
      try {
        const allowed = await window.workloopRealm.ensureMicrophoneAccess();
        if (!allowed) {
          listeningRef.current = false;
          setListening(false);
          dispatch(addNotification({
            type: 'error',
            message: 'Allow microphone access to capture captions',
          }));
          return;
        }
      } catch {
        // Chromium may still prompt via getUserMedia.
      }
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
    } catch {
      listeningRef.current = false;
      setListening(false);
      dispatch(addNotification({
        type: 'error',
        message: 'Allow microphone access to capture captions',
      }));
      return;
    }

    try {
      await ensureNote('<p></p>');
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: error?.response?.data?.error || error?.message || 'Failed to create caption note',
      }));
    }

    restartFailsRef.current = 0;
    const webOk = isElectronShell() ? false : startWebSpeech();
    if (webOk) {
      dispatch(addNotification({
        type: 'success',
        message: 'Listening — captions are saved in Notes',
      }));
      return;
    }
    const nativeOk = await startNativeCaptions();
    if (!nativeOk) {
      listeningRef.current = false;
      setListening(false);
      stopListeningHardware();
      return;
    }
    dispatch(addNotification({
      type: 'success',
      message: 'Listening — captions are saved in Notes',
    }));
  }, [dispatch, ensureNote, startNativeCaptions, startWebSpeech, stopListeningHardware]);

  const toggleListening = useCallback(() => {
    if (listeningRef.current) {
      stopListening();
      return;
    }
    startListening();
  }, [startListening, stopListening]);

  const stopListeningRef = useRef(stopListening);
  stopListeningRef.current = stopListening;

  useEffect(() => subscribeAudioCaptionEnabled(setAudioEnabled), []);

  useEffect(() => {
    if (!listening) return undefined;
    const id = window.setInterval(() => {
      if (!listeningRef.current || fatalRef.current) return;
      const idleMs = Date.now() - lastResultAtRef.current;
      if (idleMs >= 30000) {
        stopListeningRef.current?.();
        dispatch(addNotification({
          type: 'info',
          message: 'Stopped listening — no speech for 30 seconds',
        }));
        return;
      }
      const micDead = !streamRef.current?.getAudioTracks?.().some((track) => track.readyState === 'live');
      if (micDead) {
        ensureMicStream().catch(() => {});
      }
      if (isElectronShell() || !SpeechRecognitionAPI) {
        const nativeDead = !tapRef.current && !tickRef.current && !livePushRef.current;
        if (nativeDead) {
          startNativeCaptions().catch(() => {});
        }
        return;
      }
      if (!recognitionRef.current && !speechStartingRef.current) {
        startWebSpeechRef.current?.();
      }
    }, 400);
    return () => window.clearInterval(id);
  }, [dispatch, ensureMicStream, listening, startNativeCaptions]);

  useEffect(() => {
    if (!listening) return undefined;
    const resume = () => {
      if (!listeningRef.current || fatalRef.current) return;
      if (document.visibilityState === 'hidden') return;
      if (!isElectronShell() && SpeechRecognitionAPI && !recognitionRef.current && !speechStartingRef.current) {
        startWebSpeechRef.current?.();
      }
    };
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('focus', resume);
    window.addEventListener('pageshow', resume);
    return () => {
      document.removeEventListener('visibilitychange', resume);
      window.removeEventListener('focus', resume);
      window.removeEventListener('pageshow', resume);
    };
  }, [listening]);

  useEffect(() => {
    if (audioEnabled) return;
    if (listeningRef.current) stopListening();
  }, [audioEnabled, stopListening]);

  useEffect(() => () => {
    listeningRef.current = false;
    stopListeningHardware();
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    const html = htmlRef.current || '';
    if (stripNoteHtml(html) || noteIdRef.current) {
      flushSave();
    }
  }, [flushSave, stopListeningHardware]);

  const handlePointerDown = (event) => {
    if (event.type === 'mousedown' && event.button !== 0) return;
    const point = event.touches?.[0] || event;
    const rect = bubbleRef.current.getBoundingClientRect();
    dragOffsetRef.current = { x: point.clientX - rect.left, y: point.clientY - rect.top };
    pointerStartRef.current = { x: point.clientX, y: point.clientY };
    dragMovedRef.current = false;
    setIsDragging(true);
    if (event.type === 'touchstart') event.preventDefault();
  };

  useEffect(() => {
    if (!isDragging) return undefined;
    const onMove = (event) => {
      const point = event.touches?.[0] || event;
      const dx = point.clientX - pointerStartRef.current.x;
      const dy = point.clientY - pointerStartRef.current.y;
      if (Math.hypot(dx, dy) > 6) dragMovedRef.current = true;
      setPosition(constrainPosition({
        x: point.clientX - dragOffsetRef.current.x,
        y: point.clientY - dragOffsetRef.current.y,
      }));
      if (event.cancelable) event.preventDefault();
    };
    let finished = false;
    const onUp = () => {
      if (finished) return;
      finished = true;
      const dragged = dragMovedRef.current;
      setIsDragging(false);
      if (!dragged) toggleListening();
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [isDragging, toggleListening]);

  if (!user || !audioEnabled) return null;

  const preview = livePreview.length > 72 ? `…${livePreview.slice(-72)}` : livePreview;

  return (
    <div
      ref={bubbleRef}
      className={`fixed z-[78] ${isDragging ? 'cursor-grabbing scale-105' : 'cursor-grab'}`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        touchAction: 'none',
      }}
      onMouseDown={handlePointerDown}
      onTouchStart={handlePointerDown}
    >
      {listening && preview ? (
        <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 max-w-[14rem] rounded-lg bg-gray-900/90 text-white text-[11px] leading-snug px-2 py-1.5 shadow-lg pointer-events-none">
          {preview}
        </div>
      ) : null}
      <button
        type="button"
        onClick={(event) => event.stopPropagation()}
        className={`relative rounded-full shadow-lg flex items-center justify-center text-white transition-[opacity,transform] ${
          listening
            ? 'audio-caption-blink bg-rose-500 dark:bg-rose-600'
            : 'bg-primary-600 dark:bg-primary-700'
        }`}
        style={{
          width: BUBBLE_SIZE,
          height: BUBBLE_SIZE,
          opacity: 0.75,
        }}
        title={listening ? 'Stop capturing captions' : 'Start capturing captions into Notes'}
        aria-pressed={listening}
        aria-label={listening ? 'Stop live captions' : 'Start live captions'}
      >
        <MicIcon className="w-5 h-5" />
      </button>
    </div>
  );
};

export default DraggableAudioCaptionBubble;
